import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import OrdersList from './components/OrdersList';
import AddOrder from './components/AddOrder';
import OrderDetails from './components/OrderDetails';
import EditOrder from './components/EditOrder';
import DispatchManagement from './components/DispatchManagement';
import SimpleBrandSpecs from './components/SimpleBrandSpecs';
import AddBrandSpecs from './components/AddBrandSpecs';
import EditBrandSpecs from './components/EditBrandSpecs';
import ViewBrandSpecs from './components/ViewBrandSpecs';
import Login from './components/Login';
import UserManagement from './components/UserManagement';
import ViewOnlyBrandSpecs from './components/ViewOnlyBrandSpecs';
import OrderNotes from './components/OrderNotes';
import DataManagement from './components/DataManagement';
import { orderService, brandSpecService } from './services/api';
import { authService } from './services/authService';

// Global database management functions
window.seedSampleData = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seed-data`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
    const result = await response.json();
    console.log('✅ SAMPLE DATA SEEDED!', result);
    alert(`Sample data created: ${result.brands_created} brands, ${result.orders_created} orders. Refresh to see data.`);
  } catch (error) {
    console.error('Error seeding data:', error);
    alert(`Error seeding data: ${error.message}`);
  }
};

window.clearAllDatabaseData = async () => {
  if (!confirm('Are you sure you want to clear ALL database data? This cannot be undone!')) {
    return;
  }
  
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clear-all-data`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    });
    const result = await response.json();
    console.log('✅ ALL DATABASE DATA CLEARED!', result);
    alert(`Database cleared: ${result.deleted.orders} orders, ${result.deleted.brand_specs} brands deleted. Refresh to see changes.`);
  } catch (error) {
    console.error('Error clearing data:', error);
    alert(`Error clearing data: ${error.message}`);
  }
};

window.checkStatus = async () => {
  try {
    const [ordersResponse, brandsResponse] = await Promise.all([
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders`),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand-specs`)
    ]);
    
    const orders = await ordersResponse.json();
    const brands = await brandsResponse.json();
    
    alert(`🗄️ Database Status:\n${orders.length} orders\n${brands.length} brand specifications\n\nData stored on server database`);
  } catch (error) {
    console.error('Error checking status:', error);
    alert(`Error checking database status: ${error.message}`);
  }
};
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Layout Component
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main>{children}</main>
    </div>
  );
};

function App() {

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Login Route */}
          <Route path="/login" element={<Login />} />
          
          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <Layout>
                  <OrdersList />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/orders/:orderNumber"
            element={
              <ProtectedRoute>
                <Layout>
                  <OrderDetails />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/orders/:orderNumber/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <EditOrder />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/orders/:orderNumber/dispatch"
            element={
              <ProtectedRoute>
                <Layout>
                  <DispatchManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/orders/:orderNumber/notes"
            element={
              <ProtectedRoute>
                <Layout>
                  <OrderNotes />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/add-order"
            element={
              <ProtectedRoute>
                <Layout>
                  <AddOrder />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/brand-specs"
            element={
              <ProtectedRoute>
                <Layout>
                  <SimpleBrandSpecs />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/brand-specs/add"
            element={
              <ProtectedRoute>
                <Layout>
                  <AddBrandSpecs />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand-specs/edit/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <EditBrandSpecs />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand-specs/view/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <ViewBrandSpecs />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <div className="p-8 text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">Reports</h1>
                    <p className="text-gray-600">Reports functionality coming soon...</p>
                  </div>
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/user-management"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/brand-specs/view-only/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <ViewOnlyBrandSpecs />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/brand-specs/view-only/name/:brandName"
            element={
              <ProtectedRoute>
                <Layout>
                  <ViewOnlyBrandSpecs />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/brand-specs/view-only/brand/:brandId"
            element={
              <ProtectedRoute>
                <Layout>
                  <ViewOnlyBrandSpecs />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/orders/:id/notes"
            element={
              <ProtectedRoute>
                <Layout>
                  <OrderNotes />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/data-management"
            element={
              <ProtectedRoute>
                <Layout>
                  <DataManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster />
      </BrowserRouter>
    </div>
  );
}

window.helpCommands = () => {
  const mode = getStorageMode();
  const modeText = mode === STORAGE_MODES.DATABASE ? 'DATABASE' : 'LOCAL STORAGE';
  
  console.log(`
🎯 OrderTrack Pro - Testing Commands
Current Mode: ${modeText}

📦 STORAGE MANAGEMENT:
- switchToDatabase() - Use database storage (persists across devices)
- switchToLocalStorage() - Use browser localStorage (browser only)
- checkStorageMode() - Check current mode

📊 STATUS & DATA:
- checkStatus() - Check current data status
- seedSampleData() - Add sample data (database mode only)
- clearAllDatabaseData() - Clear database (database mode only)  
- localStorage.clear() - Clear local storage (local mode only)

🔧 DATABASE TOOLS (Database Mode Only):
- fixOrderStatuses() - Fix invalid order statuses

💡 TIP: Use the profile dropdown in the header to switch storage modes!
  `);
};

// Alias for backward compatibility
window.storageStatus = window.helpCommands;

export default App;